import { useMemo } from 'react';
import { getNormalizedServerParameterDate } from '@hungryroot/datetime';
import { useFeatureFlag } from '@hungryroot/feature-flags';
import { log } from '@hungryroot/o11y';
import { type OrderDateParam } from '@hungryroot/rest';

interface OrderDateSource {
  id: number;
  cutoff_datetime: string;
  inventory_date: string | null;
  release_date?: string | null;
}

/**
 * Whether the web client sends `release_date` instead of `inventory_date`.
 * `undefined` while the flag is loading; `false` when it is off or errors.
 */
export function useReleaseDateEnabled(): boolean | undefined {
  const flag = useFeatureFlag('web.feature-flag.release-date.enabled');
  return flag === undefined ? undefined : flag === true;
}

function getInventoryDate({
  id,
  cutoff_datetime,
  inventory_date,
}: OrderDateSource) {
  if (!inventory_date) {
    log('No `inventory_date` provided in getOrderDateParam.', {
      cutoff_datetime,
      inventory_date,
      orderId: id,
    });
  }

  return inventory_date
    ? inventory_date
    : getNormalizedServerParameterDate(cutoff_datetime);
}

/**
 * The order date to scope catalog, inventory and pricing requests by. See
 * `OrderDateParam` in `@hungryroot/rest` for why the param name travels with
 * the value.
 *
 * With `releaseDateEnabled`, sends the order's `release_date`. An order
 * without one falls back to its `inventory_date` rather than a cutoff-derived
 * release date: the backend resolves `release_date` by looking up real orders,
 * so a guessed date either 400s or resolves to a different week, while an
 * `inventory_date` is always accepted. Not logged, since past orders predating
 * `release_date` legitimately take this path.
 */
export function getOrderDateParam(
  order: OrderDateSource,
  releaseDateEnabled: boolean
): OrderDateParam {
  if (releaseDateEnabled && order.release_date) {
    return { release_date: order.release_date };
  }

  return { inventory_date: getInventoryDate(order) };
}

/**
 * `getOrderDateParam` for `order`, or `undefined` until both the order and the
 * release-date flag have resolved. Waiting on the flag (rather than defaulting
 * to `inventory_date` while it loads) means a request is only ever sent with
 * its final param, instead of once as `inventory_date` and again as
 * `release_date` when the flag resolves on. The flag definitions are cached,
 * so this only waits on a cold load.
 *
 * Date-scoped queries should gate on the result, not just on the order, or
 * they fire without a date while the flag is pending.
 */
export function useOrderDateParam(
  order: OrderDateSource | undefined
): OrderDateParam | undefined {
  const releaseDateEnabled = useReleaseDateEnabled();
  const id = order?.id;
  const cutoffDatetime = order?.cutoff_datetime;
  const inventoryDate = order?.inventory_date;
  const releaseDate = order?.release_date;

  // Memoized on primitives so the result is stable enough to sit in a query
  // key, and so the missing-date logs fire once per order, not once per render.
  return useMemo(() => {
    if (
      id === undefined ||
      cutoffDatetime === undefined ||
      releaseDateEnabled === undefined
    ) {
      return undefined;
    }

    return getOrderDateParam(
      {
        id,
        cutoff_datetime: cutoffDatetime,
        inventory_date: inventoryDate ?? null,
        release_date: releaseDate,
      },
      releaseDateEnabled
    );
  }, [id, cutoffDatetime, inventoryDate, releaseDate, releaseDateEnabled]);
}

/**
 * True while an order is known but its order date is still waiting on the
 * release-date flag. For queries that also run without an order (and so can't
 * gate on the order date alone): hold them while this is true so they don't
 * fire once without a date and again with it.
 */
export function isOrderDatePending(
  order: unknown,
  orderDate: OrderDateParam | undefined
) {
  return order != null && orderDate === undefined;
}
